import { PlusIcon } from "@heroicons/react/solid"
import { Breadcrumbs } from "components/breadcrumbs"
import Button from "components/button"
import { Link } from "react-router-dom"
import { Filter, SupportQACard, SupportQATable } from "./components"
import { useTable, useUpdate } from "@refinedev/core"
import { useMemo } from "react"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import { ISupportQAItem } from "interfaces"
import toast from "react-hot-toast"
import { isHaveActionPermission } from "utills/helpers"

export const SupportQAList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<ISupportQAItem>({
    resource: "cs/support-center/qas",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "data",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      category_id: logicalFilters.find((item) => item.field === "category_id")?.value || "",
    };
  }, [filters]);

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("support_qa", "cs", "edit")) return;
    if (window.confirm(`Are you sure you want to ${!isActive ? "activate": "deactivate"} this Q&A?`)) {
      updateMutation({
        resource: "cs/support-center/qas",
        id,
        meta: {
          URLSuffix: !isActive ? "active" : "inactive",
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission("support_qa", "cs", "create") && {
          additionalLabel: (
            <Link to={"/cs/support-qa/create"}>
              <Button>
                <span className="sr-only">Create New Q&A</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Create New Q&A</span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <SupportQACard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <SupportQATable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_items ?? 0,
                total: tableQuery?.data?.paging.total_qas
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQuery.isLoading) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}